/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import {
  User,
  UserPreferences,
  AuditEntry,
  CreateAuditEntryInput,
  AuthStatus,
} from '../types';
import { supabase } from '../../services/supabaseClient';
import { isSupabaseConfigured } from '../../utils/env';

/**
 * User Context State
 */
interface UserState {
  user: User | null;
  preferences: UserPreferences;
  auditEntries: AuditEntry[];
  authStatus: AuthStatus;
  isLoading: boolean;
  error: string | null;
}

/**
 * User Context Actions
 */
type UserAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'SET_PREFERENCES'; payload: UserPreferences }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<UserPreferences> }
  | { type: 'SET_AUDIT_ENTRIES'; payload: AuditEntry[] }
  | { type: 'ADD_AUDIT_ENTRY'; payload: AuditEntry }
  | { type: 'SET_AUTH_STATUS'; payload: AuthStatus };

/**
 * User Context Value
 */
interface UserContextValue {
  // State
  user: User | null;
  preferences: UserPreferences;
  auditEntries: AuditEntry[];
  authStatus: AuthStatus;
  isLoading: boolean;
  error: string | null;

  // User Actions
  loadUser: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  logoutUser: () => Promise<void>;

  // Preferences Actions
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setLanguage: (language: string) => void;

  // Audit Actions
  loadAuditEntries: (propertyId: string, limit?: number) => Promise<void>;
  createAuditEntry: (input: CreateAuditEntryInput) => Promise<void>;

  // Auth Actions
  checkAuthStatus: () => Promise<void>;
}

/**
 * User Reducer
 */
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_USER':
      return { ...state, user: action.payload, isLoading: false };

    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
        isLoading: false,
      };

    case 'SET_PREFERENCES':
      return { ...state, preferences: action.payload, isLoading: false };

    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
        isLoading: false,
      };

    case 'SET_AUDIT_ENTRIES':
      return { ...state, auditEntries: action.payload, isLoading: false };

    case 'ADD_AUDIT_ENTRY':
      return {
        ...state,
        auditEntries: [action.payload, ...state.auditEntries],
        isLoading: false,
      };

    case 'SET_AUTH_STATUS':
      return { ...state, authStatus: action.payload, isLoading: false };

    default:
      return state;
  }
};

/**
 * Default Preferences
 */
const defaultPreferences: UserPreferences = {
  theme: 'system',
  language: 'en',
  currency: 'EUR',
  dateFormat: 'DD/MM/YYYY',
  timezone: 'Europe/Athens',
  notifications: {
    email: true,
    push: true,
    priceAlerts: true,
    bookingAlerts: true,
    systemAlerts: true,
  },
};

/**
 * Initial State
 */
const initialState: UserState = {
  user: null,
  preferences: defaultPreferences,
  auditEntries: [],
  authStatus: {
    isAuthenticated: false,
    isSupabaseConfigured: isSupabaseConfigured(),
  },
  isLoading: true,
  error: null,
};

/**
 * User Context
 */
const UserContext = createContext<UserContextValue | undefined>(undefined);

/**
 * User Provider Component
 */
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  /**
   * Load User
   */
  const loadUser = useCallback(async () => {
    console.log(
      '[UserContext] loadUser called, isSupabaseConfigured:',
      state.authStatus.isSupabaseConfigured
    );

    try {
      if (state.authStatus.isSupabaseConfigured) {
        // Use getSession() for faster local storage check
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const authUser = session?.user;

        if (authUser) {
          let profileData = null;
          try {
            const { data } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', authUser.id)
              .maybeSingle();

            profileData = data;
          } catch {
            // Profile doesn't exist yet, that's ok
          }

          const user: User = {
            id: authUser.id,
            name:
              profileData?.name ||
              authUser.user_metadata?.['full_name'] ||
              authUser.email ||
              'User',
            email: authUser.email || '',
            role: profileData?.role || 'Staff',
            avatar:
              profileData?.avatar_url || authUser.user_metadata?.['avatar_url'],
            preferences: profileData?.preferences || defaultPreferences,
            createdAt: new Date(authUser.created_at),
            lastLogin: new Date(),
          };

          dispatch({ type: 'SET_USER', payload: user });

          if (user.preferences) {
            dispatch({ type: 'SET_PREFERENCES', payload: user.preferences });
          }
        } else {
          dispatch({ type: 'SET_USER', payload: null });
        }
      } else {
        dispatch({ type: 'SET_USER', payload: null });
      }
    } catch (error) {
      console.error('Error loading user:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load user' });
    }
  }, [state.authStatus.isSupabaseConfigured]);

  /**
   * Update User
   */
  const updateUser = useCallback(
    async (updates: Partial<User>) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        if (state.authStatus.isSupabaseConfigured && state.user) {
          const { error } = await supabase
            .from('user_profiles')
            .update({
              name: updates.name,
              role: updates.role,
              avatar_url: updates.avatar,
              preferences: updates.preferences,
            })
            .eq('id', state.user.id);

          if (error) throw error;
        }

        dispatch({ type: 'UPDATE_USER', payload: updates });
      } catch (error) {
        console.error('Error updating user:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update user' });
        throw error;
      }
    },
    [state.authStatus.isSupabaseConfigured, state.user]
  );

  /**
   * Logout User
   */
  const logoutUser = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      if (state.authStatus.isSupabaseConfigured) {
        await supabase.auth.signOut();
      }

      dispatch({ type: 'SET_USER', payload: null });
      dispatch({
        type: 'SET_AUTH_STATUS',
        payload: { ...state.authStatus, isAuthenticated: false },
      });
    } catch (error) {
      console.error('Error logging out:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to logout' });
      throw error;
    }
  }, [state.authStatus]);

  /**
   * Update Preferences
   */
  const updatePreferences = useCallback(
    async (updates: Partial<UserPreferences>) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const newPreferences = { ...state.preferences, ...updates };

        if (state.authStatus.isSupabaseConfigured && state.user) {
          const { error } = await supabase
            .from('user_profiles')
            .update({ preferences: newPreferences })
            .eq('id', state.user.id);

          if (error) throw error;
        }

        dispatch({ type: 'UPDATE_PREFERENCES', payload: updates });

        // Save to localStorage as fallback
        localStorage.setItem(
          'user-preferences',
          JSON.stringify(newPreferences)
        );
      } catch (error) {
        console.error('Error updating preferences:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to update preferences',
        });
        throw error;
      }
    },
    [state.preferences, state.authStatus.isSupabaseConfigured, state.user]
  );

  /**
   * Set Theme
   */
  const setTheme = useCallback(
    (theme: 'light' | 'dark' | 'system') => {
      updatePreferences({ theme });

      // Apply theme to document
      const root = window.document.documentElement;
      if (theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
          .matches
          ? 'dark'
          : 'light';
        root.classList.toggle('dark', systemTheme === 'dark');
      } else {
        root.classList.toggle('dark', theme === 'dark');
      }
    },
    [updatePreferences]
  );

  /**
   * Set Language
   */
  const setLanguage = useCallback(
    (language: string) => {
      updatePreferences({ language: language as 'en' | 'el' | 'de' | 'fr' });
    },
    [updatePreferences]
  );

  /**
   * Load Audit Entries
   */
  const loadAuditEntries = useCallback(
    async (propertyId: string, limit: number = 50) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { data, error } = await supabase
          .from('audit_logs')
          .select('*')
          .eq('property_id', propertyId)
          .order('timestamp', { ascending: false })
          .limit(limit);

        if (error) throw error;

        const auditEntries: AuditEntry[] = (data || []).map((item) => ({
          id: item.id as string,
          timestamp: new Date(item.timestamp as string),
          user: item.user_name as string,
          action: item.action as string,
          entity: item.entity as string,
          entityId: item.entity_id as string,
          changes: item.changes as Record<
            string,
            { from: unknown; to: unknown }
          >,
          ipAddress: item.ip_address as string,
          userAgent: item.user_agent as string,
          propertyId: item.property_id as string,
        }));

        dispatch({ type: 'SET_AUDIT_ENTRIES', payload: auditEntries });
      } catch (error) {
        console.error('Error loading audit entries:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to load audit entries',
        });
      }
    },
    []
  );

  /**
   * Create Audit Entry
   */
  const createAuditEntry = useCallback(
    async (input: CreateAuditEntryInput) => {
      try {
        const newEntry: AuditEntry = {
          ...input,
          id: Date.now().toString(),
          timestamp: new Date(),
          user: state.user?.name || 'Unknown',
          ipAddress: '192.168.1.100', // Mock IP
          userAgent: navigator.userAgent,
        };

        dispatch({ type: 'ADD_AUDIT_ENTRY', payload: newEntry });

        if (state.authStatus.isSupabaseConfigured) {
          await supabase.from('audit_logs').insert({
            id: newEntry.id,
            property_id: newEntry.propertyId,
            user_id: state.user?.id,
            user_name: newEntry.user,
            action: newEntry.action,
            entity: newEntry.entity,
            entity_id: newEntry.entityId,
            changes: newEntry.changes,
            ip_address: newEntry.ipAddress,
            user_agent: newEntry.userAgent,
            timestamp: newEntry.timestamp.toISOString(),
          });
        }
      } catch (error) {
        console.error('Error creating audit entry:', error);
      }
    },
    [state.user, state.authStatus.isSupabaseConfigured]
  );

  /**
   * Check Auth Status
   * Uses getSession() for fast local check, then validates with getUser() if needed
   */
  const checkAuthStatus = useCallback(async () => {
    console.log('[UserContext] checkAuthStatus called');
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const configured = isSupabaseConfigured();
      console.log('[UserContext] isSupabaseConfigured:', configured);

      if (configured) {
        // First try getSession() - this is fast and reads from local storage
        console.log('[UserContext] Calling supabase.auth.getSession()...');
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[UserContext] Session error:', sessionError);
          dispatch({
            type: 'SET_AUTH_STATUS',
            payload: {
              isSupabaseConfigured: configured,
              isAuthenticated: false,
            },
          });
          return;
        }

        if (session?.user) {
          console.log('[UserContext] Session found, user:', session.user.email);
          dispatch({
            type: 'SET_AUTH_STATUS',
            payload: {
              isSupabaseConfigured: configured,
              isAuthenticated: true,
            },
          });
          return;
        }

        // No session found - check if we're on the auth callback page
        // If so, don't set authenticated=false yet, let the callback complete
        if (window.location.pathname === '/auth/callback') {
          console.log(
            '[UserContext] On auth callback page, waiting for auth to complete...'
          );
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        console.log('[UserContext] No session found, user not authenticated');
        dispatch({
          type: 'SET_AUTH_STATUS',
          payload: { isSupabaseConfigured: configured, isAuthenticated: false },
        });
      } else {
        console.log('[UserContext] Supabase not configured');
        dispatch({
          type: 'SET_AUTH_STATUS',
          payload: { isSupabaseConfigured: false, isAuthenticated: false },
        });
      }
    } catch (error) {
      console.error('[UserContext] Error checking auth status:', error);
      dispatch({
        type: 'SET_AUTH_STATUS',
        payload: { isSupabaseConfigured: false, isAuthenticated: false },
      });
    }
  }, []);

  // Check auth status on mount and set up auth state listener
  useEffect(() => {
    checkAuthStatus();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(
        '[UserContext] Auth state changed:',
        event,
        session?.user?.email
      );
      console.log(
        '[UserContext] DEBUG: event check:',
        event === 'SIGNED_IN',
        event === 'INITIAL_SESSION',
        'session.user:',
        !!session?.user
      );
      // Handle both SIGNED_IN and INITIAL_SESSION (page reload with existing session)
      if (
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
        session?.user
      ) {
        console.log('[UserContext] ENTERED IF BLOCK - loading profile');
        // User signed in or session restored - load their profile
        const authUser = session.user;
        console.log(
          '[UserContext] Loading profile for user:',
          authUser.id,
          authUser.email
        );

        // Set user immediately with basic info (non-blocking)
        const basicUser: User = {
          id: authUser.id,
          name:
            authUser.user_metadata?.['full_name'] || authUser.email || 'User',
          email: authUser.email || '',
          role: 'Staff',
          avatar: authUser.user_metadata?.['avatar_url'],
          preferences: defaultPreferences,
          createdAt: new Date(authUser.created_at),
          lastLogin: new Date(),
        };

        console.log(
          '[UserContext] Setting basic user immediately:',
          basicUser.name,
          basicUser.email
        );
        dispatch({ type: 'SET_USER', payload: basicUser });
        dispatch({
          type: 'SET_AUTH_STATUS',
          payload: { isSupabaseConfigured: true, isAuthenticated: true },
        });

        // Fetch full profile in background (non-blocking)
        (async () => {
          try {
            console.log('[UserContext] Fetching full profile in background...');
            const { data: profileData, error: profileError } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', authUser.id)
              .maybeSingle();

            if (profileError) {
              console.warn('[UserContext] Profile query error:', profileError);
              return;
            }

            const profile = profileData as {
              name?: string;
              role?: string;
              avatar_url?: string;
              preferences?: UserPreferences;
            } | null;

            if (profile) {
              // Update user with full profile data
              const fullUser: User = {
                id: authUser.id,
                name: profile.name || basicUser.name,
                email: authUser.email || '',
                role: profile.role || 'Staff',
                avatar: profile.avatar_url || basicUser.avatar,
                preferences: profile.preferences || defaultPreferences,
                createdAt: new Date(authUser.created_at),
                lastLogin: new Date(),
              };

              console.log(
                '[UserContext] Updating user with full profile:',
                fullUser.name
              );
              dispatch({ type: 'SET_USER', payload: fullUser });

              if (fullUser.preferences) {
                dispatch({
                  type: 'SET_PREFERENCES',
                  payload: fullUser.preferences,
                });
              }
            } else {
              // Create profile if it doesn't exist
              console.log('[UserContext] Creating new user profile...');
              await supabase.from('user_profiles').insert({
                id: authUser.id,
                name: authUser.user_metadata?.['full_name'] || authUser.email,
                email: authUser.email,
                avatar_url: authUser.user_metadata?.['avatar_url'],
                role: 'Staff',
              });
            }
          } catch (err) {
            console.warn('[UserContext] Background profile fetch failed:', err);
            // User is already set with basic info, no action needed
          }
        })();
      } else if (event === 'SIGNED_OUT') {
        // User signed out
        dispatch({ type: 'SET_USER', payload: null });
        dispatch({
          type: 'SET_AUTH_STATUS',
          payload: { isSupabaseConfigured: true, isAuthenticated: false },
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [checkAuthStatus]);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const savedPreferences = localStorage.getItem('user-preferences');
      if (savedPreferences) {
        const preferences = JSON.parse(savedPreferences);
        dispatch({ type: 'SET_PREFERENCES', payload: preferences });

        // Apply theme
        const theme = preferences.theme || 'system';
        const root = window.document.documentElement;
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
            .matches
            ? 'dark'
            : 'light';
          root.classList.toggle('dark', systemTheme === 'dark');
        } else {
          root.classList.toggle('dark', theme === 'dark');
        }
      }
    } catch (error) {
      console.error('Error loading preferences from localStorage:', error);
    }
  }, []);

  const value = useMemo<UserContextValue>(
    () => ({
      user: state.user,
      preferences: state.preferences,
      auditEntries: state.auditEntries,
      authStatus: state.authStatus,
      isLoading: state.isLoading,
      error: state.error,
      loadUser,
      updateUser,
      logoutUser,
      updatePreferences,
      setTheme,
      setLanguage,
      loadAuditEntries,
      createAuditEntry,
      checkAuthStatus,
    }),
    [
      state.user,
      state.preferences,
      state.auditEntries,
      state.authStatus,
      state.isLoading,
      state.error,
      loadUser,
      updateUser,
      logoutUser,
      updatePreferences,
      setTheme,
      setLanguage,
      loadAuditEntries,
      createAuditEntry,
      checkAuthStatus,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * useUser Hook
 * Custom hook to access User context
 */
export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export default UserContext;
